import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { checkSession, getUser, startSession } from '../../utils/session';
import { Button, Field, Input } from '../../components/admin/ui';
import { IconAlert, IconLock, IconPackage } from '../../components/icons';
import SiteCredit from '../../components/SiteCredit';

const promises = [
  'See the delivery issues customers report against your network.',
  'Issue trends, postcode hotspots and the retailers behind them.',
  'Built from real reports, not surveys or estimates.'
];

const CourierLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Already signed in as a courier? Skip the form.
  useEffect(() => {
    if (checkSession('courier') && getUser('courier')?.role === 'courier') {
      navigate('/courier/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setLoading(true);

    try {
      const response = await api.post('/api/auth/courier/login', formData);
      startSession('courier', {
        token: response.data.token,
        user: response.data.user,
        context: response.data.courierAccount
      });
      navigate('/courier/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      setErrorCode(err.response?.data?.code || '');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* Brand panel — desktop only. Blue, the way couriers read everywhere else. */}
      <div className="relative hidden w-[46%] max-w-2xl flex-col justify-between overflow-hidden bg-info-900 p-12 lg:flex">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-info-600/40 blur-3xl"
          aria-hidden="true"
        />
        <Link
          to="/"
          className="relative flex w-fit items-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label="Back to ParcelBuddy"
        >
          <img src="/logo.png" alt="" className="h-12 w-12 rounded-full" />
        </Link>

        <div className="relative">
          <h2 className="font-display text-[34px] font-800 leading-[1.15] tracking-tight text-white">
            Courier
            <br />
            <span className="text-info-300">intelligence.</span>
          </h2>
          <ul className="mt-8 space-y-4">
            {promises.map((promise) => (
              <li key={promise} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-info-400/20 text-info-300">
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="text-[15px] leading-relaxed text-white/80">{promise}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-white/60">
          No integration required. Sign up and your dashboard is there the same day.
        </p>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <img src="/brand.png" alt="ParcelBuddy" className="h-16 w-auto" />
            <p className="mt-2 text-[11px] font-500 uppercase tracking-[0.16em] text-info-600">
              Courier portal
            </p>
          </div>

          <div className="rounded-2xl border border-ink-200 bg-white p-7 shadow-card sm:p-8">
            <div className="mb-6">
              <h1 className="font-display text-xl font-700 tracking-tight text-ink-900">Sign in</h1>
              <p className="mt-1.5 text-sm text-ink-500">
                Use the account you registered, or the one our team set up for you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-lg bg-danger-50 px-3.5 py-3 ring-1 ring-inset ring-danger-100"
                >
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-danger-600" />
                  <div className="min-w-0">
                    <p className="text-sm text-danger-700">{error}</p>
                    {errorCode === 'NO_PASSWORD_SET' && (
                      <p className="mt-1 text-xs text-danger-700/80">
                        Your account exists but has no password yet.
                      </p>
                    )}
                    {errorCode === 'COURIER_SUSPENDED' && (
                      <p className="mt-1 text-xs text-danger-700/80">
                        Sign-in stays blocked until the account is reinstated.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <Field label="Email address" htmlFor="email" required>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="username"
                  placeholder="you@yourcompany.co.uk"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Field>

              <Field label="Password" htmlFor="password" required>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="pr-16"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-xs font-600 text-ink-500 transition-colors hover:text-info-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>

              <Button type="submit" size="lg" loading={loading} className="w-full">
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>

              <p className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-info-600 hover:text-info-700 hover:underline"
                >
                  Forgot your password?
                </Link>
              </p>
            </form>

            <div className="mt-6 flex items-start gap-2 border-t border-ink-100 pt-5 text-xs text-ink-500">
              <IconLock size={14} className="mt-px shrink-0" />
              <p>Sessions end automatically after 24 hours of inactivity.</p>
            </div>
          </div>

          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-ink-500">
            <IconPackage size={13} />
            No account yet?{' '}
            <Link to="/couriers/start-pilot" className="font-600 text-info-600 hover:underline">
              Start a free pilot
            </Link>
          </p>
          <SiteCredit className="mt-3 text-center" />
        </div>
      </div>
    </div>
  );
};

export default CourierLogin;
